import React from "react";
import SportsGymnasticsIcon from "@mui/icons-material/SportsGymnastics";
import FitnessCenterIcon from "@mui/icons-material/FitnessCenter";
import TuneIcon from "@mui/icons-material/Tune";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import ExercisePlanMultiSelectDropdown from "@components/ui/customDropdowns/ExercisePlanMultiSelectDropdown";
import MedicalViewMultipSelectDropdown from "@components/ui/customDropdowns/MedicalViewMultipSelectDropdown";

const ExercisePreferences = ({
  getFieldValue,
  getDynamicDropdownOptions,
  handleMultiSelectChange,
  handleSelectChange,
  getDisplayName,
  getDisplayMap,
  errors,
  isAdviceLoading,
  daysOptions,
  exerciseTimeOptions,
  t,
}) => {
  return (
    <div className="mt-6 p-4 border border-gray-200 rounded-lg bg-gray-50">
      <h3 className="text-lg font-semibold text-gray-800 mb-3">
        {t("Exercise Preferences")}
      </h3>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="col-span-2 p-4 bg-blue-50 border-l-4 border-blue-500 rounded-lg shadow-sm">
          <div className="flex items-center mb-2">
            <SportsGymnasticsIcon className="text-blue-500 mr-2" />
            <span className="text-xs font-semibold text-gray-700 uppercase">
              {t("Workout Categories")}
            </span>
          </div>
          <ExercisePlanMultiSelectDropdown
            options={getDynamicDropdownOptions("workoutsCategories").map(
              (opt) => opt.value
            )}
            selectedValues={getFieldValue("workoutsCategories") || []}
            onSelect={(values) =>
              handleMultiSelectChange("workoutsCategories", values)
            }
            label={t("Workout Categories")}
            hasError={!!errors.workoutsCategories}
            errorMessage={errors.workoutsCategories}
            placeholder={t("preferences.validation.onlyOne")}
            isEditable={!isAdviceLoading}
            isFistForm={true}
            displayMap={getDisplayMap("workoutsCategories")}
            fieldKey="workoutsCategories"
          />
          <div className="mt-2 flex flex-wrap gap-1">
            {getFieldValue("workoutsCategories") &&
              getFieldValue("workoutsCategories").length > 0 &&
              getFieldValue("workoutsCategories").map((categoryId) => (
                <span
                  key={categoryId}
                  className="px-2 py-1 text-xs font-medium rounded-md bg-blue-100 text-blue-800 border border-blue-200 flex items-center space-x-1 cursor-pointer transition duration-150 ease-in-out hover:bg-blue-200"
                >
                  <span>
                    {getDisplayName("workoutsCategories", categoryId)}
                  </span>
                  <button
                    type="button"
                    onClick={() =>
                      handleMultiSelectChange(
                        "workoutsCategories",
                        (getFieldValue("workoutsCategories") || []).filter(
                          (id) => id !== categoryId
                        )
                      )
                    }
                    className="ml-1 w-4 h-4 rounded-full text-blue-600 hover:text-blue-800 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    aria-label={`Remove workout category`}
                    disabled={isAdviceLoading}
                  >
                    <svg
                      className="w-4 h-4"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth="2"
                        d="M6 18L18 6M6 6l12 12"
                      ></path>
                    </svg>
                  </button>
                </span>
              ))}
          </div>
        </div>

        <div className="col-span-2 lg:col-span-1 p-4 bg-green-50 border-l-4 border-green-500 rounded-lg shadow-sm">
          <div className="flex items-center mb-2">
            <FitnessCenterIcon className="text-green-500 mr-2" />
            <span className="text-xs font-semibold text-gray-700 uppercase">
              {t("Equipment Category")}
            </span>
          </div>
          <div>
            <select
              className={`border p-2 rounded-md w-full h-10 text-sm outline-none bg-white ${
                errors.equipmentCategories ? "border-red-500" : ""
              } ${isAdviceLoading ? "opacity-70 cursor-default" : ""}`}
              value={getFieldValue("equipmentCategories") || ""}
              onChange={(e) =>
                handleSelectChange("equipmentCategories", e.target.value)
              }
              disabled={isAdviceLoading}
            >
              <option value="" disabled>
                {t("preferences.validation.onlyOne")}
              </option>
              {getDynamicDropdownOptions("equipmentCategories").map(
                (option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                )
              )}
            </select>
            {errors["equipmentCategories"] && (
              <div className="text-red-500 text-[10px] md:text-[12px] ml-2 mt-1">
                {errors["equipmentCategories"]}
              </div>
            )}
            <div className="mt-2">
              {getFieldValue("equipmentCategories") && (
                <span className="px-2 py-1 text-xs font-medium rounded-md bg-green-100 text-green-800 border border-green-200">
                  {getDisplayName(
                    "equipmentCategories",
                    getFieldValue("equipmentCategories")
                  )}
                </span>
              )}
            </div>
          </div>
        </div>

        <div className="col-span-2 lg:col-span-1 p-4 bg-purple-50 border-l-4 border-purple-500 rounded-lg shadow-sm">
          <div className="flex items-center mb-2">
            <TuneIcon className="text-purple-500 mr-2" />
            <span className="text-xs font-semibold text-gray-700 uppercase">
              {t("Difficulty Level")}
            </span>
          </div>
          <div>
            <select
              className={`border p-2 rounded-md w-full h-10 text-sm outline-none bg-white ${
                errors.difficultiesLevel ? "border-red-500" : ""
              } ${isAdviceLoading ? "opacity-70 cursor-default" : ""}`}
              value={getFieldValue("difficultiesLevel") || ""}
              onChange={(e) =>
                handleSelectChange("difficultiesLevel", e.target.value)
              }
              disabled={isAdviceLoading}
            >
              <option value="" disabled>
                {t("preferences.validation.onlyOne")}
              </option>
              {getDynamicDropdownOptions("difficultiesLevel").map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
            {errors["difficultiesLevel"] && (
              <div className="text-red-500 text-[10px] md:text-[12px] ml-2 mt-1">
                {errors["difficultiesLevel"]}
              </div>
            )}
            <div className="mt-2">
              {getFieldValue("difficultiesLevel") && (
                <span className="px-2 py-1 text-xs font-medium rounded-md bg-purple-100 text-purple-800 border border-purple-200">
                  {getDisplayName(
                    "difficultiesLevel",
                    getFieldValue("difficultiesLevel")
                  )}
                </span>
              )}
            </div>
          </div>
        </div>

        <div className="col-span-2 p-4 bg-orange-50 border-l-4 border-orange-500 rounded-lg shadow-sm">
          <div className="flex items-center mb-2">
            <AccessTimeIcon className="text-orange-500 mr-2" />
            <span className="text-xs font-semibold text-gray-700 uppercase">
              {t("Daily Exercise Time Preference")}
            </span>
          </div>
          <div>
            <select
              className={`border p-2 rounded-md w-full h-10 text-sm outline-none bg-white ${
                errors.dailyExerciseTimePreference ? "border-red-500" : ""
              } ${isAdviceLoading ? "opacity-70 cursor-default" : ""}`}
              value={getFieldValue("dailyExerciseTimePreference") || ""}
              onChange={(e) =>
                handleSelectChange(
                  "dailyExerciseTimePreference",
                  e.target.value
                )
              }
              disabled={isAdviceLoading}
            >
              <option value="" disabled>
                {t("preferences.validation.onlyOne")}
              </option>
              {exerciseTimeOptions.map((option) => (
                <option key={option} value={option}>
                  {t(
                    `allPreferencesModule.options.dailyExerciseTimePreference.${option}`
                  )}
                </option>
              ))}
            </select>
            {errors["dailyExerciseTimePreference"] && (
              <div className="text-red-500 text-[10px] md:text-[12px] ml-2 mt-1">
                {errors["dailyExerciseTimePreference"]}
              </div>
            )}
            <div className="mt-2">
              {getFieldValue("dailyExerciseTimePreference") && (
                <span className="px-2 py-1 text-xs font-medium rounded-md bg-orange-100 text-orange-800 border border-orange-200">
                  {t(
                    `allPreferencesModule.options.dailyExerciseTimePreference.${getFieldValue(
                      "dailyExerciseTimePreference"
                    )}`
                  )}
                </span>
              )}
            </div>
          </div>
        </div>

        <div className="col-span-2 md:col-span-2 p-4 bg-red-50 border-l-4 border-red-500 rounded-lg shadow-sm">
          <div className="flex items-center mb-2">
            <CalendarMonthIcon className="text-red-500 mr-2" />
            <span className="text-xs font-semibold text-gray-700 uppercase">
              {t("Selectable Days")}
            </span>
          </div>
          <MedicalViewMultipSelectDropdown
            options={daysOptions?.map((opt) => ({
              value: opt,
              label: t(`allPreferencesModule.options.selectableDays.${opt}`),
            }))}
            selectedValues={getFieldValue("selectableDays") || []}
            onSelect={(selectedOptions) => {
              const originalValues = selectedOptions.map((opt) =>
                typeof opt === "object" ? opt.value : opt
              );
              handleMultiSelectChange("selectableDays", originalValues);
            }}
            isFistForm={true}
            hasError={!!errors.selectableDays}
            errorMessage={errors.selectableDays}
            label={t("Selectable Days")}
            placeholder={t("preferences.validation.onlyOne")}
            isEditable={!isAdviceLoading}
          />
          <div className="mt-2 flex flex-wrap gap-1">
            {getFieldValue("selectableDays") &&
              getFieldValue("selectableDays").length > 0 &&
              getFieldValue("selectableDays").map((day) => (
                <span
                  key={day}
                  className="px-2 py-1 text-xs font-medium rounded-md bg-red-100 text-red-800 border border-red-200 flex items-center space-x-1 cursor-pointer transition duration-150 ease-in-out hover:bg-red-200"
                >
                  <span>
                    {t(`allPreferencesModule.options.selectableDays.${day}`)}
                  </span>
                  <button
                    type="button"
                    onClick={() =>
                      handleMultiSelectChange(
                        "selectableDays",
                        (getFieldValue("selectableDays") || []).filter(
                          (d) => d !== day
                        )
                      )
                    }
                    className="ml-1 w-4 h-4 rounded-full text-red-600 hover:text-red-800 focus:outline-none focus:ring-2 focus:ring-red-500"
                    aria-label={`Remove ${day}`}
                    disabled={isAdviceLoading}
                  >
                    <svg
                      className="w-4 h-4"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth="2"
                        d="M6 18L18 6M6 6l12 12"
                      ></path>
                    </svg>
                  </button>
                </span>
              ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ExercisePreferences;
